/**
 * G1 — after platform OIDC lands (often `/account?oidcLand=1`), consume the
 * path-tenant park stashed via `resolveOidcLoginReturnTo` when bare
 * `/{tenant}/shop` could not be passed as BE OIDC returnTo.
 *
 * Publishes G2 post-auth navigate handoff via `navigateToPostAuthReturnTarget`
 * (G1 handoff lifetime / deferred clear lives there — shared with password/OTP).
 *
 * R1 — wait on land via `window.location.search` SSOT +
 * `OIDC_LAND_QUERY_CHANGED_EVENT` (same as RequireGuestAuth /
 * RequireCustomerAuth) so strip/pending→false re-runs restore.
 *
 * G4 — `oidcOfferPhone=1` is landing-pending (via {@link hasOidcLandingPending}).
 * Also depend on RR `location.search` so offer skip/complete (`navigate` off
 * add-phone) re-runs restore after the offer query clears — hint is not stranded.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomerSession } from '../shared/session/CustomerSessionProvider.js';
import {
  clearOidcPostAuthTenantHint,
  navigateToPostAuthReturnTarget,
  peekOidcPostAuthTenantHint,
} from '../shared/session/returnTarget.js';
import {
  hasOidcLandingPending,
  hasOidcOfferPhonePending,
  OIDC_LAND_QUERY_CHANGED_EVENT,
} from '../shared/session/oidcCookieLand.js';

function subscribeOidcLandWindowSearch(onStoreChange: () => void): () => void {
  window.addEventListener(OIDC_LAND_QUERY_CHANGED_EVENT, onStoreChange);
  return () => {
    window.removeEventListener(OIDC_LAND_QUERY_CHANGED_EVENT, onStoreChange);
  };
}

function getOidcLandWindowSearchSnapshot(): string {
  return window.location.search;
}

function getOidcLandWindowSearchServerSnapshot(): string {
  return '';
}

export function useOidcPostAuthReturnRestore(): void {
  const { session, sessionHydrated } = useCustomerSession();
  const navigate = useNavigate();
  const { search: routerSearch } = useLocation();
  const ranForHintRef = useRef<string | null>(null);

  // R1 — subscribe to strip events; snapshot is window.search (SSOT).
  const windowOidcSearch = useSyncExternalStore(
    subscribeOidcLandWindowSearch,
    getOidcLandWindowSearchSnapshot,
    getOidcLandWindowSearchServerSnapshot,
  );

  useEffect(() => {
    if (!sessionHydrated || session === null) {
      ranForHintRef.current = null;
      return;
    }
    // Land strip uses history.replaceState → window.search is SSOT (RequireGuestAuth).
    // Do NOT OR full RR search for oidcLand — RR often still shows ?oidcLand=1 after
    // replaceState strip and would strand restore forever.
    const liveWindowSearch =
      typeof window !== 'undefined' ? window.location.search : windowOidcSearch;
    if (hasOidcLandingPending(liveWindowSearch)) {
      return;
    }
    // G4 — offer skip/complete navigates via RR; hold while router still has offer.
    if (hasOidcOfferPhonePending(routerSearch)) {
      return;
    }
    const hint = peekOidcPostAuthTenantHint();
    if (hint === null) {
      return;
    }
    if (ranForHintRef.current === hint) {
      return;
    }
    ranForHintRef.current = hint;
    clearOidcPostAuthTenantHint();
    // resolvePostAuthReturnTarget fail-closes auth-loop parks (/sign-in);
    // hint is org tenant code only — never ?tenant= login bind.
    // G1 lifetime: navigateToPostAuthReturnTarget publish+navigate schedules
    // SoftRace clear — after settle, later /sign-in remount cannot steal park.
    navigateToPostAuthReturnTarget(hint, navigate, { replace: true });
  }, [session, sessionHydrated, navigate, windowOidcSearch, routerSearch]);
}
